import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from '@env';
import { catchError, defer, switchMap, take, throwError, timeout } from 'rxjs';

export function isOwnApiUrl(url: string): boolean {
  try {
    const origin = globalThis.location?.origin ?? 'http://localhost';
    const api = new URL(environment.apiBaseUrl, origin);
    const target = new URL(url, origin);
    const apiPath = api.pathname.replace(/\/$/, '');
    return target.origin === api.origin &&
      (target.pathname === apiPath || target.pathname.startsWith(`${apiPath}/`));
  } catch {
    return false;
  }
}

function sameAccount(expected: AccountInfo, actual: AccountInfo | null): boolean {
  return !!actual && expected.homeAccountId === actual.homeAccountId &&
    expected.localAccountId === actual.localAccountId && expected.tenantId === actual.tenantId;
}

@Injectable()
export class ApiTokenRecoveryInterceptor implements HttpInterceptor {
  private readonly msal = inject(MsalService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    const account = this.msal.instance.getActiveAccount();
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401 ||
          !isOwnApiUrl(req.url) || !account || !environment.auth.scopes.length ||
          !/^Bearer \S+$/i.test(req.headers.get('Authorization') ?? '') ||
          !sameAccount(account, this.msal.instance.getActiveAccount())) {
          return throwError(() => error);
        }

        return defer(() => this.msal.acquireTokenSilent({
          account,
          scopes: [...environment.auth.scopes],
          forceRefresh: true,
        })).pipe(
          timeout({ first: 15000 }),
          take(1),
          catchError(() => throwError(() => error)),
          switchMap(result => {
            if (!result.accessToken || !sameAccount(account, result.account) ||
              !sameAccount(account, this.msal.instance.getActiveAccount())) {
              return throwError(() => error);
            }
            return next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } }));
          }),
        );
      }),
    );
  }
}